import React from 'react';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';
import TopNavigation from '../../components/TopNavigation';
import Footer from '../../components/Footer';

const ManufacturingUseCaseDetail = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNavigation />
      
      {/* Hero Section */}
     <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      
      {/* Left Column - Text Content */}
      <div>
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
          style={{ backgroundColor: 'rgba(79, 179, 184, 0.15)', border: '1px solid rgba(79, 179, 184, 0.4)', color: '#4FB3B8' }}
        >
          Robotics & Manufacturing
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#E8EDDF' }}>
          Orchestration<br />Beyond Automation
        </h1>
        
        <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: '#9CA3AF' }}>
          Modern factories are complex systems of systems—robotic cells, quality inspection agents, predictive maintenance models, and human operators working in parallel. The challenge is no longer automating individual machines. It is orchestrating coherent behavior across distributed intelligence.
        </p>
        
        <p className="text-sm md:text-base leading-relaxed" style={{ color: '#9CA3AF' }}>
          Rigid, siloed automation fails when conditions change. <span style={{ color: '#E8EDDF', fontWeight: '600' }}>Sleeve coordinates adaptive intelligence</span>—deploying collaborative autonomy where machines, sensors, legacy systems, and humans perceive, decide, and act in unison.
        </p>
      </div>
      
      {/* Right Column - Diagram */}
      <div className="flex items-center justify-center">
        <div 
          className="w-full rounded-xl p-6"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <svg width="100%" height="380" viewBox="0 0 380 380" className="max-w-full">
            
            {/* Top Row - Robot Cells */}
            <rect x="50" y="30" width="70" height="50" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
            <text x="85" y="60" textAnchor="middle" fill="#4FB3B8" fontSize="10" fontWeight="600">ROBOT</text>
            <text x="85" y="92" textAnchor="middle" fill="#9CA3AF" fontSize="8">Cell A</text>
            
            <rect x="155" y="30" width="70" height="50" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
            <text x="190" y="60" textAnchor="middle" fill="#4FB3B8" fontSize="10" fontWeight="600">ROBOT</text>
            <text x="190" y="92" textAnchor="middle" fill="#9CA3AF" fontSize="8">Cell B</text>
            
            <rect x="260" y="30" width="70" height="50" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
            <text x="295" y="60" textAnchor="middle" fill="#4FB3B8" fontSize="10" fontWeight="600">ROBOT</text>
            <text x="295" y="92" textAnchor="middle" fill="#9CA3AF" fontSize="8">Cell C</text>
            
            {/* Sensor Circles */}
            <circle cx="85" cy="115" r="16" fill="#4FB3B8" />
            <text x="85" y="119" textAnchor="middle" fill="#242423" fontSize="8" fontWeight="600">SENS</text>
            
            <circle cx="190" cy="115" r="16" fill="#4FB3B8" />
            <text x="190" y="119" textAnchor="middle" fill="#242423" fontSize="8" fontWeight="600">SENS</text>
            
            <circle cx="295" cy="115" r="16" fill="#4FB3B8" />
            <text x="295" y="119" textAnchor="middle" fill="#242423" fontSize="8" fontWeight="600">SENS</text>
            
            {/* Connection lines from sensors to orchestration */}
            <line x1="85" y1="131" x2="120" y2="175" stroke="#9CA3AF" strokeWidth="1.5" />
            <line x1="190" y1="131" x2="190" y2="175" stroke="#9CA3AF" strokeWidth="1.5" />
            <line x1="295" y1="131" x2="260" y2="175" stroke="#9CA3AF" strokeWidth="1.5" />
            
            {/* Sleeve Orchestration Box */}
            <rect x="70" y="175" width="240" height="70" rx="6" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
            <text x="190" y="200" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="700">SLEEVE ORCHESTRATION</text>
            <text x="190" y="218" textAnchor="middle" fill="#9CA3AF" fontSize="9">Coordinated Intelligence Layer</text>
            <text x="190" y="234" textAnchor="middle" fill="#9CA3AF" fontSize="7">Event Router • Policy Engine • State Sync</text>
            
            {/* Connection lines from orchestration to bottom row */}
            <line x1="110" y1="245" x2="85" y2="280" stroke="#9CA3AF" strokeWidth="1.5" />
            <line x1="190" y1="245" x2="190" y2="275" stroke="#9CA3AF" strokeWidth="1.5" />
            <line x1="270" y1="245" x2="295" y2="280" stroke="#9CA3AF" strokeWidth="1.5" />
            
            {/* Bottom Row */}
            {/* Legacy PLC */}
            <rect x="25" y="280" width="100" height="50" rx="4" fill="#242423" stroke="#3A3A38" strokeWidth="1.5" />
            <text x="75" y="305" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">LEGACY PLC</text>
            <text x="75" y="320" textAnchor="middle" fill="#9CA3AF" fontSize="8">SCADA</text>
            
            {/* Human Operator Circle - Dashed border */}
            <circle cx="190" cy="310" r="35" fill="#242423" stroke="#F5CB5C" strokeWidth="2" strokeDasharray="5 3" />
            <text x="190" y="315" textAnchor="middle" fill="#F5CB5C" fontSize="10" fontWeight="600">HUMAN</text>
            <text x="190" y="360" textAnchor="middle" fill="#9CA3AF" fontSize="8">Operator</text>
            
            {/* Cloud */}
            <rect x="255" y="280" width="100" height="50" rx="4" fill="#242423" stroke="#3A3A38" strokeWidth="1.5" />
            <text x="305" y="305" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="600">CLOUD</text>
            <text x="305" y="320" textAnchor="middle" fill="#9CA3AF" fontSize="8">Analytics</text>
            
          </svg>
        </div>
      </div>
      
    </div>
  </div>
</section>

      {/* System Context Section */}
      <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    
    {/* Section Header */}
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#E8EDDF' }}>
        Sleeve as the Connective Tissue
      </h2>
      <p className="text-base md:text-lg max-w-4xl mx-auto leading-relaxed" style={{ color: '#9CA3AF' }}>
        Sleeve deploys collaborative autonomy across edge and cloud—transforming isolated robots into a cohesive intelligent collective. Systems perceive conditions, decide responses, and act in coordinated unison.
      </p>
    </div>
    
    {/* Diagram */}
    <div 
      className="rounded-xl p-8 md:p-12 relative overflow-hidden"
      style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
    >
      {/* Grid Pattern Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(79, 179, 184, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(79, 179, 184, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '25px 25px'
        }}
      ></div>
      
      <div className="relative z-10">
        
        {/* Top Row - Assembly, Welding, QA Vision, Packaging */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          
          {/* Assembly */}
          <div 
            className="relative rounded-lg p-6 text-center"
            style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
          >
            <div 
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: '#4FB3B8', color: '#242423' }}
            >
              1
            </div>
            <svg className="w-8 h-8 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="6" rx="1" />
              <rect x="3" y="12" width="18" height="6" rx="1" />
            </svg>
            <div className="text-sm font-semibold" style={{ color: '#4FB3B8' }}>ASSEMBLY</div>
            <div className="text-xs mt-1" style={{ color: '#4FB3B8' }}>●</div>
          </div>
          
          {/* Welding */}
          <div 
            className="relative rounded-lg p-6 text-center"
            style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
          >
            <div 
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: '#4FB3B8', color: '#242423' }}
            >
              2
            </div>
            <svg className="w-8 h-8 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="5" rx="1" />
              <rect x="3" y="11" width="18" height="5" rx="1" />
              <line x1="7" y1="18" x2="17" y2="18" />
            </svg>
            <div className="text-sm font-semibold" style={{ color: '#4FB3B8' }}>WELDING</div>
            <div className="text-xs mt-1" style={{ color: '#4FB3B8' }}>●</div>
          </div>
          
          {/* QA Vision */}
          <div 
            className="relative rounded-lg p-6 text-center"
            style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
          >
            <div 
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: '#4FB3B8', color: '#242423' }}
            >
              3
            </div>
            <svg className="w-8 h-8 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <rect x="5" y="3" width="14" height="10" rx="2" />
              <line x1="12" y1="13" x2="12" y2="17" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
            <div className="text-sm font-semibold" style={{ color: '#4FB3B8' }}>QA VISION</div>
            <div className="text-xs mt-1" style={{ color: '#4FB3B8' }}>●</div>
          </div>
          
          {/* Packaging */}
          <div 
            className="relative rounded-lg p-6 text-center"
            style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
          >
            <div 
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ backgroundColor: '#4FB3B8', color: '#242423' }}
            >
              4
            </div>
            <svg className="w-8 h-8 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <path d="M12 3L12 15M12 15L8 11M12 15L16 11" />
              <path d="M4 17h16v4H4z" />
            </svg>
            <div className="text-sm font-semibold" style={{ color: '#4FB3B8' }}>PACKAGING</div>
            <div className="text-xs mt-1" style={{ color: '#4FB3B8' }}>●</div>
          </div>
          
        </div>
        
        {/* Wavy Connection - Top */}
        <div className="flex justify-center mb-6">
          <svg width="300" height="40" viewBox="0 0 300 40">
            <path 
              d="M 30 5 Q 75 5 100 20 Q 125 35 150 35 Q 175 35 200 20 Q 225 5 270 5" 
              stroke="#9CA3AF" 
              strokeWidth="2" 
              strokeDasharray="6 4"
              fill="none"
            />
          </svg>
        </div>
        
        {/* Sleeve Orchestration Box */}
        <div 
          className="rounded-lg p-6 mb-6 text-center relative overflow-hidden"
          style={{ backgroundColor: '#242423', border: '2px solid #4FB3B8' }}
        >
          {/* Inner grid pattern */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(79, 179, 184, 0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(79, 179, 184, 0.5) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px'
            }}
          ></div>
          <div className="relative z-10 flex items-center justify-center gap-3">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              <circle cx="4" cy="10" r="2" />
              <circle cx="20" cy="10" r="2" />
            </svg>
            <span className="text-base font-bold" style={{ color: '#E8EDDF' }}>SLEEVE ORCHESTRATION</span>
          </div>
          <div className="relative z-10 text-xs mt-2" style={{ color: '#9CA3AF' }}>
            Coordinated Scheduling • Adaptive Response • Policy Enforcement
          </div>
        </div>
        
        {/* Wavy Connection - Bottom */}
        <div className="flex justify-center mb-6">
          <svg width="300" height="40" viewBox="0 0 300 40">
            <path 
              d="M 30 35 Q 75 35 100 20 Q 125 5 150 5 Q 175 5 200 20 Q 225 35 270 35" 
              stroke="#F5CB5C" 
              strokeWidth="2" 
              strokeDasharray="6 4"
              fill="none"
            />
          </svg>
        </div>
        
        {/* Bottom Row - Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          
          <div 
            className="rounded-full py-3 px-6 text-center"
            style={{ backgroundColor: 'transparent', border: '2px solid #F5CB5C' }}
          >
            <span className="text-sm font-medium" style={{ color: '#E8EDDF' }}>Adjusted</span>
          </div>
          
          <div 
            className="rounded-full py-3 px-6 text-center"
            style={{ backgroundColor: 'transparent', border: '2px solid #F5CB5C' }}
          >
            <span className="text-sm font-medium" style={{ color: '#E8EDDF' }}>Paused</span>
          </div>
          
          <div 
            className="rounded-full py-3 px-6 text-center"
            style={{ backgroundColor: 'transparent', border: '2px solid #F5CB5C' }}
          >
            <span className="text-sm font-medium" style={{ color: '#E8EDDF' }}>Alerted</span>
          </div>
          
          <div 
            className="rounded-full py-3 px-6 text-center"
            style={{ backgroundColor: 'transparent', border: '2px solid #F5CB5C' }}
          >
            <span className="text-sm font-medium" style={{ color: '#E8EDDF' }}>Resumed</span>
          </div>
          
        </div>
        
      </div>
    </div>
    
  </div>
</section>

<section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    
    {/* Header */}
    <div 
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
      style={{ backgroundColor: 'rgba(255, 107, 107, 0.15)', border: '1px solid rgba(255, 107, 107, 0.4)', color: '#FF6B6B' }}
    >
      Use Case 1
    </div>
    
    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#E8EDDF' }}>
      Remote Maintenance
    </h2>
    
    <p className="text-xl md:text-2xl mb-12" style={{ color: '#4FB3B8' }}>
      From Passive Monitoring to Collaborative Resilience
    </p>
    
    {/* Three Column Content */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      
      {/* Problem Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#FF6B6B' }}>
          Problem
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>High mean time to repair (MTTR)</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Costly emergency site visits</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Remote troubleshooting without physical context</span>
          </li>
        </ul>
      </div>
      
      {/* Sleeve Advantage Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#4FB3B8' }}>
          Sleeve Advantage
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Live digital twin synchronized with physical assets</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Local diagnostic agents + remote human experts</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Automated tests executed at the edge</span>
          </li>
        </ul>
      </div>
      
      {/* Outcome Column */}
      <div 
        className="rounded-lg p-6"
        style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
      >
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#4FB3B8' }}>
          Outcome
        </h4>
        <div className="text-4xl md:text-5xl font-bold mb-3" style={{ color: '#E8EDDF' }}>
          40%
        </div>
        <p className="text-sm" style={{ color: '#9CA3AF' }}>
          Reduction in equipment downtime through coordinated remote diagnostics
        </p>
      </div>
      
    </div>
    
    {/* Diagram */}
    <div 
      className="rounded-xl p-6 md:p-8"
      style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        
        {/* Physical Asset + Edge Agent Group */}
        <div 
          className="flex items-center gap-3 rounded-lg p-4 flex-1 min-w-[200px]"
          style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
        >
          {/* Physical Asset */}
          <div className="flex items-center gap-2 flex-1">
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="12" rx="2" />
              <line x1="3" y1="20" x2="21" y2="20" />
            </svg>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{ color: '#E8EDDF' }}>PHYSICAL ASSET</span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#FF6B6B">
                  <path d="M12 2L1 21h22L12 2zm0 4l7.5 13h-15l10-5 10 5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  <circle cx="12" cy="16" r="1"/>
                  <rect x="11" y="10" width="2" height="4"/>
                </svg>
              </div>
              <span className="text-xs" style={{ color: '#9CA3AF' }}>Factory Floor</span>
            </div>
          </div>
          
          {/* Edge Agent Button */}
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-md"
            style={{ backgroundColor: '#4FB3B8' }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#242423" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            <span className="text-xs font-semibold" style={{ color: '#242423' }}>EDGE AGENT</span>
          </div>
        </div>
        
        {/* Arrow */}
        <svg className="w-8 h-8 flex-shrink-0 hidden md:block" viewBox="0 0 24 24" fill="none" stroke="#F5CB5C" strokeWidth="2">
          <path d="M5 12h14M13 5l7 7-7 7"/>
        </svg>
        
        {/* Digital Twin + Sleeve Group */}
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          {/* Digital Twin */}
          <div 
            className="flex items-center gap-3 rounded-lg p-4 flex-1"
            style={{ backgroundColor: '#242423', border: '2px dashed #4FB3B8' }}
          >
            <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
              <circle cx="12" cy="12" r="9" strokeDasharray="4 2"/>
            </svg>
            <div>
              <span className="text-xs font-semibold" style={{ color: '#4FB3B8' }}>DIGITAL TWIN</span>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: '#9CA3AF' }}>Real-Time Sync</span>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#9CA3AF' }}></span>
              </div>
            </div>
          </div>
          
          {/* Sleeve Button */}
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-md flex-shrink-0"
            style={{ backgroundColor: '#4FB3B8' }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#242423" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
              <line x1="15" y1="3" x2="15" y2="21"/>
            </svg>
            <span className="text-xs font-semibold" style={{ color: '#242423' }}>SLEEVE</span>
          </div>
        </div>
        
        {/* Arrow */}
        <svg className="w-8 h-8 flex-shrink-0 hidden md:block" viewBox="0 0 24 24" fill="none" stroke="#F5CB5C" strokeWidth="2">
          <path d="M5 12h14M13 5l7 7-7 7"/>
        </svg>
        
        {/* Remote Expert + Tests Group */}
        <div 
          className="flex items-center gap-3 rounded-lg p-4 flex-1 min-w-[200px]"
          style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
        >
          {/* Remote Expert */}
          <div className="flex items-center gap-3 flex-1">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ border: '2px dashed #F5CB5C' }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#F5CB5C" strokeWidth="1.5">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
              </svg>
            </div>
            <div>
              <span className="text-xs font-semibold" style={{ color: '#E8EDDF' }}>REMOTE EXPERT</span>
              <div className="text-xs" style={{ color: '#9CA3AF' }}>Human Oversight</div>
            </div>
          </div>
          
          {/* Tests Button */}
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-md flex-shrink-0"
            style={{ backgroundColor: 'transparent', border: '1px solid #3A3A38' }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l-1-8z"/>
            </svg>
            <span className="text-xs font-medium" style={{ color: '#9CA3AF' }}>TESTS</span>
          </div>
        </div>
        
      </div>
    </div>
    
  </div>
</section>

      {/* USE CASE 2 - Automated Agent Orchestration */}
<section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    
    {/* Header */}
    <div 
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
      style={{ backgroundColor: 'rgba(255, 107, 107, 0.15)', border: '1px solid rgba(255, 107, 107, 0.4)', color: '#FF6B6B' }}
    >
      Use Case 2
    </div>
    
    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#E8EDDF' }}>
      Automated Agent Orchestration
    </h2>
    
    <p className="text-xl md:text-2xl mb-12" style={{ color: '#4FB3B8' }}>
      Harmonizing the Multi-Agent Factory Floor
    </p>
    
    {/* Three Column Content */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      
      {/* Problem Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#FF6B6B' }}>
          Problem
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Siloed intelligent agents operating independently</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Resource scheduling conflicts and bottlenecks</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#FF6B6B' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>No adaptive response when anomalies detected</span>
          </li>
        </ul>
      </div>
      
      {/* Sleeve Advantage Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#4FB3B8' }}>
          Sleeve Advantage
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>System-of-systems orchestrator for multi-agent coordination</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Decentralized coordination between QA, production, supply chain</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Real-time adaptive response to detected anomalies</span>
          </li>
        </ul>
      </div>
      
      {/* Outcome Column */}
      <div 
        className="rounded-lg p-6"
        style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
      >
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#4FB3B8' }}>
          Outcome
        </h4>
        <div className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#E8EDDF' }}>
          Improved OEE
        </div>
        <p className="text-sm" style={{ color: '#9CA3AF' }}>
          Self-balancing, elastic production workflows with coordinated agent intelligence
        </p>
      </div>
      
    </div>
    
    {/* Diagram */}
    <div 
      className="rounded-xl p-6 md:p-8"
      style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
    >
      
      {/* Top Row - Production, QA Vision, Supply */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        
        {/* Production */}
        <div 
          className="rounded-lg p-5"
          style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6 mb-3" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="5" rx="1" />
            <rect x="3" y="11" width="18" height="5" rx="1" />
          </svg>
          <div className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>PRODUCTION</div>
          <div className="text-xs" style={{ color: '#9CA3AF' }}>Active</div>
        </div>
        
        {/* QA Vision */}
        <div 
          className="rounded-lg p-5 relative"
          style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
        >
          <div className="flex justify-between items-start">
            <svg className="w-6 h-6 mb-3" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="1.5">
              <rect x="5" y="3" width="14" height="10" rx="2" />
              <line x1="12" y1="13" x2="12" y2="17" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#F5CB5C">
              <path d="M12 2L1 21h22L12 2z"/>
            </svg>
          </div>
          <div className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>QA VISION</div>
          <div className="text-xs" style={{ color: '#9CA3AF' }}>DEFECT</div>
        </div>
        
        {/* Supply */}
        <div 
          className="rounded-lg p-5"
          style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6 mb-3" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
            <path d="M12 3L12 15M12 15L8 11M12 15L16 11" />
            <path d="M4 17h16v4H4z" />
          </svg>
          <div className="text-sm font-semibold mb-1" style={{ color: '#9CA3AF' }}>SUPPLY</div>
          <div className="text-xs" style={{ color: '#9CA3AF' }}>Normal</div>
        </div>
        
      </div>
      
      {/* Sleeve Orchestration Box */}
      <div 
        className="rounded-lg p-5 mb-4 text-center"
        style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
      >
        <div className="flex items-center justify-center gap-3">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
            <circle cx="12" cy="8" r="3" />
            <path d="M5 20c0-3 3-5 7-5s7 2 7 5" />
            <circle cx="4" cy="10" r="2" />
            <circle cx="20" cy="10" r="2" />
          </svg>
          <span className="text-sm font-bold" style={{ color: '#E8EDDF' }}>SLEEVE ORCHESTRATION</span>
        </div>
        <div className="text-xs mt-2" style={{ color: '#9CA3AF' }}>
          Multi-Agent Coordination • Event Correlation
        </div>
      </div>
      
      {/* Bottom Row - Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* Reduce Speed */}
        <div 
          className="rounded-lg p-4 text-center"
          style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
        >
          <div className="text-sm font-semibold mb-2" style={{ color: '#E8EDDF' }}>REDUCE SPEED</div>
          <div className="text-xs font-semibold" style={{ color: '#4FB3B8' }}>EXECUTED</div>
        </div>
        
        {/* Increase Scan */}
        <div 
          className="rounded-lg p-4 text-center"
          style={{ backgroundColor: '#242423', border: '1px solid #FF6B6B' }}
        >
          <div className="text-sm font-semibold mb-2" style={{ color: '#E8EDDF' }}>INCREASE SCAN</div>
          <div className="text-xs font-semibold" style={{ color: '#FF6B6B' }}>EXECUTED</div>
        </div>
        
        {/* Adjust Buffer */}
        <div 
          className="rounded-lg p-4 text-center"
          style={{ backgroundColor: '#242423', border: '1px solid #F5CB5C' }}
        >
          <div className="text-sm font-semibold mb-2" style={{ color: '#E8EDDF' }}>ADJUST BUFFER</div>
          <div className="text-xs font-semibold" style={{ color: '#F5CB5C' }}>EXECUTED</div>
        </div>
        
      </div>
      
    </div>
    
  </div>
</section>

      {/* USE CASE 3 - Cybersecurity Detection & Response */}
<section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    
    {/* Header */}
    <div 
      className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
      style={{ backgroundColor: 'rgba(156, 163, 175, 0.15)', border: '1px solid rgba(156, 163, 175, 0.4)', color: '#9CA3AF' }}
    >
      Use Case 3
    </div>
    
    <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#E8EDDF' }}>
      Cybersecurity Detection & Response
    </h2>
    
    <p className="text-xl md:text-2xl mb-12" style={{ color: '#4FB3B8' }}>
      The Intelligent Immune System for OT Infrastructure
    </p>
    
    {/* Three Column Content */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      
      {/* Problem Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
          Problem
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#9CA3AF' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>IT security tools too slow or disruptive for OT</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#9CA3AF' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Detection without coordinated response is insufficient</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#9CA3AF' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Factory-wide shutdowns from single compromised asset</span>
          </li>
        </ul>
      </div>
      
      {/* Sleeve Advantage Column */}
      <div>
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#4FB3B8' }}>
          Sleeve Advantage
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Embedded anomaly detection agents at the edge</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Orchestrated isolation of compromised assets</span>
          </li>
          <li className="flex items-start gap-2">
            <span style={{ color: '#4FB3B8' }}>.</span>
            <span className="text-sm" style={{ color: '#E8EDDF' }}>Continued operation of unaffected systems</span>
          </li>
        </ul>
      </div>
      
      {/* Outcome Column */}
      <div 
        className="rounded-lg p-6"
        style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
      >
        <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
          Outcome
        </h4>
        <div className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#E8EDDF' }}>
          Instant Containment
        </div>
        <p className="text-sm" style={{ color: '#9CA3AF' }}>
          Operational continuity with IEC 62443 alignment
        </p>
      </div>
      
    </div>
    
    {/* Diagram - Before/After */}
    <div 
      className="rounded-xl p-6 md:p-8"
      style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        
        {/* BEFORE Section */}
        <div>
          {/* Before Badge */}
          <div className="flex justify-center mb-4">
            <div 
              className="px-4 py-2 rounded-md text-xs font-semibold uppercase"
              style={{ backgroundColor: '#FF6B6B', color: '#242423' }}
            >
              Before
            </div>
          </div>
          
          {/* 2x2 Grid of Robots */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Robot A */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT A</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
            
            {/* Robot B */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT B</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
            
            {/* Robot C */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT C</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
            
            {/* Robot D */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT D</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-center" style={{ color: '#9CA3AF' }}>All systems operational</p>
        </div>
        
        {/* MIDDLE Section - Threat Detected */}
        <div className="flex flex-col items-center justify-center">
          {/* Threat Detected Box */}
          <div 
            className="rounded-lg p-6 text-center mb-4 w-full max-w-[180px]"
            style={{ backgroundColor: 'rgba(255, 107, 107, 0.15)', border: '2px solid #FF6B6B' }}
          >
            <svg className="w-10 h-10 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="1.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <div className="text-sm font-semibold mb-1" style={{ color: '#FF6B6B' }}>THREAT DETECTED</div>
            <div className="text-xs" style={{ color: '#9CA3AF' }}>Robot B Anomaly</div>
          </div>
          
          {/* Arrow Down */}
          <svg className="w-6 h-6 mb-4" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2">
            <path d="M12 5v14M5 12l7 7 7-7"/>
          </svg>
          
          {/* Sleeve Box */}
          <div 
            className="rounded-lg p-4 text-center w-full max-w-[140px]"
            style={{ backgroundColor: '#242423', border: '1px solid #4FB3B8' }}
          >
            <svg className="w-8 h-8 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            <div className="text-xs font-semibold" style={{ color: '#4FB3B8' }}>SLEEVE</div>
            <div className="text-xs" style={{ color: '#9CA3AF' }}>Auto-Response</div>
          </div>
        </div>
        
        {/* AFTER Section */}
        <div>
          {/* After Badge */}
          <div className="flex justify-center mb-4">
            <div 
              className="px-4 py-2 rounded-md text-xs font-semibold uppercase"
              style={{ backgroundColor: '#4FB3B8', color: '#242423' }}
            >
              After
            </div>
          </div>
          
          {/* 2x2 Grid of Robots */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Robot A - Active */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT A</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
            
            {/* Robot B - Isolated/Quarantined */}
            <div 
              className="rounded-lg p-4 text-center relative"
              style={{ backgroundColor: '#242423', border: '2px dashed #FF6B6B' }}
            >
              {/* Quarantined Badge */}
              <div 
                className="absolute -top-2 -right-2 px-2 py-0.5 rounded text-xs font-semibold"
                style={{ backgroundColor: '#FF6B6B', color: '#242423', fontSize: '9px' }}
              >
                QUARANTINED
              </div>
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT B</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#FF6B6B' }}></span>
                <span className="text-xs" style={{ color: '#FF6B6B' }}>ISOLATED</span>
              </div>
            </div>
            
            {/* Robot C - Active */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT C</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
            
            {/* Robot D - Active */}
            <div 
              className="rounded-lg p-4 text-center"
              style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
            >
              <svg className="w-6 h-6 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="5" rx="1" />
                <rect x="3" y="11" width="18" height="5" rx="1" />
              </svg>
              <div className="text-xs font-semibold mb-1" style={{ color: '#4FB3B8' }}>ROBOT D</div>
              <div className="flex items-center justify-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#4ADE80' }}></span>
                <span className="text-xs" style={{ color: '#9CA3AF' }}>ACTIVE</span>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-center font-semibold" style={{ color: '#4FB3B8' }}>75% uptime maintained</p>
        </div>
        
      </div>
    </div>
    
  </div>
</section>

      {/* Common Capabilities */}
<section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
  <div className="max-w-7xl mx-auto">
    
    {/* Header */}
    <h2 className="text-sm font-semibold uppercase tracking-wider text-center mb-16" style={{ color: '#9CA3AF' }}>
      Common Capabilities Across All Use Cases
    </h2>
    
    {/* Four Column Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
      
      {/* Edge-Deployed Intelligence */}
      <div className="text-center">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
            <rect x="3" y="4" width="18" height="5" rx="1" />
            <rect x="3" y="12" width="18" height="5" rx="1" />
            <circle cx="7" cy="6.5" r="1" fill="#4FB3B8" />
            <circle cx="7" cy="14.5" r="1" fill="#4FB3B8" />
          </svg>
        </div>
        <h3 className="text-base font-semibold mb-3" style={{ color: '#E8EDDF' }}>
          Edge-Deployed Intelligence
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
          Agents run at the edge for sub-100ms decision loops
        </p>
      </div>
      
      {/* Human-in-the-Loop Control */}
      <div className="text-center">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#F5CB5C" strokeWidth="1.5">
            <circle cx="9" cy="7" r="3" />
            <circle cx="15" cy="7" r="3" />
            <path d="M3 18c0-3 3-5 6-5" />
            <path d="M15 13c3 0 6 2 6 5" />
            <path d="M12 13v5" />
          </svg>
        </div>
        <h3 className="text-base font-semibold mb-3" style={{ color: '#E8EDDF' }}>
          Human-in-the-Loop Control
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
          Approval gates and oversight for critical operations
        </p>
      </div>
      
      {/* Real-Time Orchestration */}
      <div className="text-center">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="1.5">
            <circle cx="12" cy="6" r="2" />
            <circle cx="6" cy="18" r="2" />
            <circle cx="18" cy="18" r="2" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="12" x2="6" y2="16" />
            <line x1="12" y1="12" x2="18" y2="16" />
          </svg>
        </div>
        <h3 className="text-base font-semibold mb-3" style={{ color: '#E8EDDF' }}>
          Real-Time Orchestration
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
          Event-driven coordination with state synchronization
        </p>
      </div>
      
      {/* Hardware-Agnostic Deployment */}
      <div className="text-center">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
            <rect x="5" y="4" width="14" height="10" rx="2" />
            <line x1="12" y1="14" x2="12" y2="17" />
            <line x1="8" y1="17" x2="16" y2="17" />
          </svg>
        </div>
        <h3 className="text-base font-semibold mb-3" style={{ color: '#E8EDDF' }}>
          Hardware-Agnostic Deployment
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
          Works with any PLC, robot, or control system
        </p>
      </div>
      
    </div>
    
  </div>
</section>
      {/* Final CTA */}
      <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8 border-b border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            See Sleeve in Action
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
            Discover how Sleeve orchestrates intelligent systems across your manufacturing infrastructure with live demonstrations and technical documentation.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Request a Live Platform Demo
              <Icon name="ArrowRight" size={16} />
            </Link>
            <Link 
              to="/use-cases" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-lg font-medium hover:bg-card/80 transition-colors"
            >
              Explore Other Use Cases
              <Icon name="ArrowRight" size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ManufacturingUseCaseDetail;